import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { facultyService } from '../api/facultyService';
import { eventService } from '../api/eventService';
import FacultyForm from '../components/FacultyForm';
import ExpertGraph from '../components/ExpertGraph';
import { Plus, Search, Globe, Briefcase, Award, Linkedin, Trash2, ChevronRight, GraduationCap, Network, List, Maximize2, Minimize2, MapPin, History, Clock } from 'lucide-react';
import ContentLoader from '../components/ContentLoader';

const Faculty = () => {
    // Removed role restrictions as per request
    const canEdit = true;
    const queryClient = useQueryClient();

    const [searchTerm, setSearchTerm] = useState('');
    const [historySearchTerm, setHistorySearchTerm] = useState('');
    const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
    const [selectedExpert, setSelectedExpert] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [editingFaculty, setEditingFaculty] = useState(null);

    // Graph view states
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'network' or 'history'
    const [isFullscreen, setIsFullscreen] = useState(false);

    const { data: faculty = [], isLoading: loading } = useQuery({
        queryKey: ['faculty'],
        queryFn: async () => {
            const data = await facultyService.getAllFaculty();
            return data || [];
        },
        staleTime: 1000 * 60 * 10, // Consider data fresh for 10 minutes
    });

    // Dynamic Invitation History Loading
    const { data: allEventsData = [] } = useQuery({
        queryKey: ['events', 'history'],
        queryFn: async () => {
            try {
                return await eventService.getAllEvents();
            } catch (err) {
                try {
                    return await eventService.getAll();
                } catch (e) {
                    console.error("Failed to fetch events for history", e);
                    return [];
                }
            }
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Extract all invite history records across all events (External Experts only)
    const invitationHistory = useMemo(() => {
        const list = [];
        allEventsData.forEach(event => {
            if (event.Panelists && Array.isArray(event.Panelists)) {
                event.Panelists.forEach(p => {
                    if (p.type === 'external') {
                        // Resolve organization from active faculty list
                        const exp = faculty.find(f => f.FacultyID === p.id);
                        list.push({
                            panelistId: p.id,
                            name: p.name,
                            organisation: exp?.Organisation || 'External Expert',
                            eventTitle: event.Title,
                            eventId: event.EventID,
                            eventDate: event.StartDateTime,
                            eventStatus: event.Status || 'Scheduled',
                            category: event.Category || event.EventType
                        });
                    }
                });
            }
        });
        return list.sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
    }, [allEventsData, faculty]);

    // Computed Invitation Stats for KPI cards
    const invitationStats = useMemo(() => {
        const total = invitationHistory.length;
        const uniqueSet = new Set(invitationHistory.map(h => h.name.trim().toLowerCase()));
        const uniqueExperts = uniqueSet.size;
        const completed = invitationHistory.filter(h => h.eventStatus === 'Completed').length;
        const upcoming = invitationHistory.filter(h => h.eventStatus === 'Scheduled' || h.eventStatus === 'In Progress').length;
        return { total, uniqueExperts, completed, upcoming };
    }, [invitationHistory]);

    // Filtered invitation history for the table view
    const filteredHistory = useMemo(() => {
        return invitationHistory.filter(h => {
            const matchesSearch = 
                h.name.toLowerCase().includes(historySearchTerm.toLowerCase()) ||
                h.eventTitle.toLowerCase().includes(historySearchTerm.toLowerCase()) ||
                (h.organisation && h.organisation.toLowerCase().includes(historySearchTerm.toLowerCase())) ||
                (h.category && h.category.toLowerCase().includes(historySearchTerm.toLowerCase()));
            const matchesStatus = historyStatusFilter === 'all' || h.eventStatus === historyStatusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [invitationHistory, historySearchTerm, historyStatusFilter]);

    // Watch for ESC key or native browser exit fullscreen
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const toggleFullscreen = async () => {
        try {
            if (!document.fullscreenElement) {
                // We want to fullscreen the main app container so the graph fills the screen
                const elem = document.documentElement; // Full viewport
                if (elem.requestFullscreen) {
                    await elem.requestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                }
            }
        } catch (err) {
            console.error('Error attempting to enable fullscreen:', err);
        }
    };

    const handleGraphNodeClick = async (expertData) => {
        setSelectedExpert(expertData);
        if (document.fullscreenElement && document.exitFullscreen) {
            await document.exitFullscreen();
        }
        setIsFullscreen(false);
        setViewMode('list');
    };



    // Auto-select the first expert if none is selected
    useEffect(() => {
        if (faculty.length > 0) {
            setSelectedExpert(prev => {
                if (!prev) return faculty[0];
                const updated = faculty.find(f => f.FacultyID === prev.FacultyID);
                return updated || prev;
            });
        }
    }, [faculty]); // Only re-run when faculty data updates from cache/network

    const filteredFaculty = useMemo(() => {
        return faculty.filter(f => {
            const matchesSearch =
                f.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                f.Organisation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                f.Designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                f.About?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (f.Skills && f.Skills.some(s => s.toLowerCase().includes(searchTerm.toLowerCase())));

            return matchesSearch;
        });
    }, [faculty, searchTerm]);

    const handleCreateSuccess = () => {
        setIsCreating(false);
        setEditingFaculty(null);
        // Instantly invalidate cache to trigger a background refetch
        queryClient.invalidateQueries({ queryKey: ['faculty'] });
    };

    const handleDelete = async (e, facultyId) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this expert profile? This action cannot be undone.')) {
            try {
                await facultyService.deleteFaculty(facultyId);
                if (selectedExpert?.FacultyID === facultyId) {
                    setSelectedExpert(null);
                }
                // Instantly invalidate cache so UI updates right away
                queryClient.invalidateQueries({ queryKey: ['faculty'] });
            } catch (error) {
                console.error('Error deleting faculty:', error);
                alert('Failed to delete expert. Please try again.');
            }
        }
    };

    const ExpertInitial = ({ name, size = 40 }) => {
        const initial = name ? name.charAt(0).toUpperCase() : '?';
        return (
            <div style={{
                width: `${size}px`, height: `${size}px`, borderRadius: '50%',
                background: 'linear-gradient(135deg, #f5f3ff 0%, #ddd6fe 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#7c3aed', fontWeight: 700, fontSize: `${size / 2.5}px`,
                border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                flexShrink: 0
            }}>
                {initial}
            </div>
        );
    };

    return (
        <div style={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', background: '#f8fafc', padding: '1rem', overflowX: 'hidden', maxWidth: '100%' }}>
            {/* Page Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexShrink: 0 }}>
                <div>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>External Experts</h1>
                    <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>Global research partners and future speakers.</p>
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {/* View Toggle */}
                    <div style={{ display: 'flex', background: '#e2e8f0', borderRadius: '8px', padding: '0.25rem' }}>
                        <button
                            onClick={() => setViewMode('list')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
                                background: viewMode === 'list' ? 'white' : 'transparent',
                                color: viewMode === 'list' ? '#1e293b' : '#64748b',
                                border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600,
                                cursor: 'pointer', transition: 'all 0.2s',
                                boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                            }}
                        >
                            <List size={16} /> List
                        </button>
                        <button
                            onClick={() => setViewMode('network')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
                                background: viewMode === 'network' ? 'white' : 'transparent',
                                color: viewMode === 'network' ? '#7c3aed' : '#64748b',
                                border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600,
                                cursor: 'pointer', transition: 'all 0.2s',
                                boxShadow: viewMode === 'network' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                            }}
                        >
                            <Network size={16} /> Network Graph
                        </button>
                        <button
                            onClick={() => setViewMode('history')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
                                background: viewMode === 'history' ? 'white' : 'transparent',
                                color: viewMode === 'history' ? '#0d9488' : '#64748b',
                                border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600,
                                cursor: 'pointer', transition: 'all 0.2s',
                                boxShadow: viewMode === 'history' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                            }}
                        >
                            <History size={16} /> Invitation History
                        </button>
                    </div>

                    {canEdit && (
                        <button
                            onClick={() => setIsCreating(true)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#2563eb', color: 'white',
                                padding: '0.6rem 1.25rem', borderRadius: '10px', fontWeight: 600, border: 'none', cursor: 'pointer',
                                fontSize: '0.9rem', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
                            }}
                        >
                            <Plus size={18} />
                            Add New Expert
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ContentLoader text="Loading experts..." />
                </div>
            ) : viewMode === 'network' ? (
                /* Network Graph View */
                <div style={{ flex: 1, position: 'relative', background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
                    <button
                        onClick={toggleFullscreen}
                        style={{
                            position: 'absolute', top: '1rem', right: '1rem', zIndex: 10,
                            background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.5rem',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)', color: '#475569', transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#7c3aed'; e.currentTarget.style.borderColor = '#ddd6fe'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                        title="View Fullscreen"
                    >
                        <Maximize2 size={20} />
                    </button>
                    <ExpertGraph data={filteredFaculty} onNodeClick={handleGraphNodeClick} />
                </div>
            ) : viewMode === 'history' ? (
                /* Dynamic Invitation History View */
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', overflow: 'hidden', maxWidth: '100%' }}>
                    {/* KPI Stats Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', flexShrink: 0 }}>
                        <div style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '1px solid #c7d2fe', padding: '0.75rem 1rem', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '0.75rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4f46e5', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', flexShrink: 0 }}>
                                <History size={18} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Invitations</div>
                                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e1b4b', marginTop: '0.1rem' }}>{invitationStats.total}</div>
                            </div>
                        </div>
                        <div style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', border: '1px solid #ddd6fe', padding: '0.75rem 1rem', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '0.75rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', flexShrink: 0 }}>
                                <Award size={18} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unique Invited</div>
                                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e1b4b', marginTop: '0.1rem' }}>{invitationStats.uniqueExperts}</div>
                            </div>
                        </div>
                        <div style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '1px solid #a7f3d0', padding: '0.75rem 1rem', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '0.75rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', flexShrink: 0 }}>
                                <GraduationCap size={18} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Completed Events</div>
                                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#064e3b', marginTop: '0.1rem' }}>{invitationStats.completed}</div>
                            </div>
                        </div>
                        <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '1px solid #bbf7d0', padding: '0.75rem 1rem', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '0.75rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', flexShrink: 0 }}>
                                <Clock size={18} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scheduled / Ongoing</div>
                                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#14532d', marginTop: '0.1rem' }}>{invitationStats.upcoming}</div>
                            </div>
                        </div>
                    </div>

                    {/* Filter & Search Controls */}
                    <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', flexShrink: 0, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', maxWidth: '100%', boxSizing: 'border-box' }}>
                        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                            <Search style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={16} />
                            <input
                                type="text"
                                placeholder="Search expert name or event title..."
                                value={historySearchTerm}
                                onChange={(e) => setHistorySearchTerm(e.target.value)}
                                style={{
                                    width: '100%', padding: '0.5rem 0.5rem 0.5rem 2.2rem', borderRadius: '8px',
                                    border: '1px solid #e2e8f0', outline: 'none', fontSize: '0.85rem', boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexShrink: 0, width: '180px' }}>
                            <select
                                value={historyStatusFilter}
                                onChange={(e) => setHistoryStatusFilter(e.target.value)}
                                style={{ 
                                    width: '100%',
                                    padding: '0.5rem 1.5rem 0.5rem 1rem', 
                                    borderRadius: '8px', 
                                    border: '1px solid #e2e8f0', 
                                    outline: 'none', 
                                    fontSize: '0.85rem', 
                                    background: 'white', 
                                    cursor: 'pointer',
                                    flexShrink: 0
                                }}
                            >
                                <option value="all">All Statuses</option>
                                <option value="Completed">Completed</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Scheduled">Scheduled</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        </div>
                    </div>

                    {/* Invitation History Table */}
                    <div style={{ flex: 1, background: 'white', borderRadius: '16px', border: '1px solid #cbd5e1', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                                <thead style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 10 }}>
                                    <tr>
                                        <th style={{ width: '35%', border: '1px solid #cbd5e1', padding: '0.75rem 1rem', fontWeight: 600, color: '#475569', background: '#f8fafc' }}>Invited Contributor</th>
                                        <th style={{ width: '35%', border: '1px solid #cbd5e1', padding: '0.75rem 1rem', fontWeight: 600, color: '#475569', background: '#f8fafc' }}>Invited Event Title</th>
                                        <th style={{ width: '12%', border: '1px solid #cbd5e1', padding: '0.75rem 1rem', fontWeight: 600, color: '#475569', background: '#f8fafc' }}>Classification</th>
                                        <th style={{ width: '10%', border: '1px solid #cbd5e1', padding: '0.75rem 1rem', fontWeight: 600, color: '#475569', background: '#f8fafc' }}>Invitation Date</th>
                                        <th style={{ width: '8%', border: '1px solid #cbd5e1', padding: '0.75rem 1rem', fontWeight: 600, color: '#475569', background: '#f8fafc' }}>Event Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredHistory.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" style={{ border: '1px solid #cbd5e1', padding: '3rem', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                                                No invitation history records found matching the active filters.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredHistory.map((row, index) => {
                                            const matchesFaculty = faculty.find(f => f.FacultyID === row.panelistId);
                                            const displayName = row.organisation && row.organisation !== 'External Expert' 
                                                ? `${row.name} - ${row.organisation}` 
                                                : row.name;

                                            // Styled badges for classifications
                                            const getCategoryStyle = (cat) => {
                                                const c = (cat || 'Event').toLowerCase();
                                                if (c.includes('conf')) return { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' }; // Blue
                                                if (c.includes('sem') || c.includes('web')) return { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe' }; // Purple
                                                if (c.includes('meet')) return { bg: '#ecfeff', text: '#155e75', border: '#cffafe' }; // Cyan
                                                if (c.includes('work')) return { bg: '#fffbeb', text: '#92400e', border: '#fef3c7' }; // Amber
                                                return { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' }; // Slate
                                            };
                                            const catStyle = getCategoryStyle(row.category);

                                            return (
                                                <tr key={index} style={{ transition: 'background 0.2s', '&:hover': { background: '#f8fafc' } }}>
                                                    <td style={{ border: '1px solid #cbd5e1', padding: '0.75rem 1rem', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                        {matchesFaculty ? (
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedExpert(matchesFaculty);
                                                                    setViewMode('list');
                                                                }}
                                                                style={{ background: 'none', border: 'none', color: '#2563eb', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: '0.9rem', textAlign: 'left', textDecoration: 'underline', wordBreak: 'break-word', whiteSpace: 'normal' }}
                                                                title="Click to view detailed biography profile"
                                                            >
                                                                {displayName}
                                                            </button>
                                                        ) : (
                                                            <span style={{ fontWeight: 600, color: '#334155', wordBreak: 'break-word', whiteSpace: 'normal' }}>{displayName}</span>
                                                        )}
                                                    </td>
                                                    <td 
                                                        style={{ border: '1px solid #cbd5e1', padding: '0.75rem 1rem', fontWeight: 500, color: '#1e293b', wordBreak: 'break-word', whiteSpace: 'normal' }}
                                                    >
                                                        {row.eventTitle}
                                                    </td>
                                                    <td style={{ border: '1px solid #cbd5e1', padding: '0.75rem 1rem', textAlign: 'center' }}>
                                                        <span style={{
                                                            fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px', fontWeight: 600,
                                                            background: catStyle.bg, color: catStyle.text, border: `1px solid ${catStyle.border}`
                                                        }}>
                                                            {row.category}
                                                        </span>
                                                    </td>
                                                    <td style={{ border: '1px solid #cbd5e1', padding: '0.75rem 1rem', color: '#64748b' }}>
                                                        {row.eventDate ? new Date(row.eventDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                                                    </td>
                                                    <td style={{ border: '1px solid #cbd5e1', padding: '0.75rem 1rem', textAlign: 'center' }}>
                                                        <span style={{
                                                            fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '99px', fontWeight: 600,
                                                            background: row.eventStatus === 'Completed' ? '#d1e7dd' : (row.eventStatus === 'Cancelled' ? '#f8d7da' : (row.eventStatus === 'In Progress' ? '#cffafe' : '#f1f5f9')),
                                                            color: row.eventStatus === 'Completed' ? '#0f5132' : (row.eventStatus === 'Cancelled' ? '#842029' : (row.eventStatus === 'In Progress' ? '#083344' : '#475569')),
                                                            border: `1px solid ${row.eventStatus === 'Completed' ? '#badbcc' : (row.eventStatus === 'Cancelled' ? '#f5c6cb' : (row.eventStatus === 'In Progress' ? '#bae6fd' : '#e2e8f0'))}`
                                                        }}>
                                                            {row.eventStatus}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ flex: 1, display: 'flex', gap: '1rem', overflow: 'hidden' }}>
                    {/* Left Panel: Expert List */}
                    <div style={{
                        flex: '0 0 320px', display: 'flex', flexDirection: 'column',
                        background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', overflow: 'hidden'
                    }}>
                        <div style={{ padding: '1rem', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                            <div style={{ position: 'relative' }}>
                                <Search style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={16} />
                                <input
                                    type="text"
                                    placeholder="Search experts..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        width: '100%', padding: '0.5rem 0.5rem 0.5rem 2.2rem', borderRadius: '8px',
                                        border: '1px solid #e2e8f0', outline: 'none', fontSize: '0.85rem'
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
                            {loading && <ContentLoader text="Loading..." />}
                            {!loading && filteredFaculty.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontSize: '0.85rem' }}>
                                    No experts found.
                                </div>
                            )}
                            {!loading && filteredFaculty.map((item) => (
                                <div
                                    key={item.FacultyID}
                                    onClick={() => setSelectedExpert(item)}
                                    style={{
                                        padding: '1rem', borderRadius: '12px', marginBottom: '0.5rem', cursor: 'pointer',
                                        transition: 'all 0.2s', border: '1px solid transparent',
                                        background: selectedExpert?.FacultyID === item.FacultyID ? '#eff6ff' : 'transparent',
                                        borderColor: selectedExpert?.FacultyID === item.FacultyID ? '#bfdbfe' : 'transparent'
                                    }}
                                >
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <ExpertInitial name={item.Name} size={36} />
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.Name}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.Organisation}
                                            </div>
                                            {(item.Country || item.Region) && (
                                                <div style={{ fontSize: '0.7rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.1rem' }}>
                                                    <MapPin size={10} />
                                                    {[item.Country, item.Region].filter(Boolean).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                        <ChevronRight size={14} color={selectedExpert?.FacultyID === item.FacultyID ? '#2563eb' : '#cbd5e1'} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Panel: Detailed View */}
                    <div style={{
                        flex: 1, display: 'flex', flexDirection: 'column',
                        background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', overflow: 'hidden'
                    }}>
                        {selectedExpert ? (
                            <>
                                {/* Detailed Header */}
                                <div style={{
                                    padding: '1.5rem',
                                    background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                                    color: '#1e1b4b', position: 'relative',
                                    borderBottom: '1px solid #ddd6fe'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
                                            <div style={{
                                                width: '64px', height: '64px', borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #ffffff 0%, #f3e8ff 100%)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#7c3aed', fontWeight: 700, fontSize: '24px',
                                                border: '2px solid #ddd6fe', boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                                flexShrink: 0
                                            }}>
                                                {selectedExpert.Name ? selectedExpert.Name.charAt(0).toUpperCase() : '?'}
                                            </div>
                                            <div>
                                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.25rem 0', color: '#1e1b4b' }}>{selectedExpert.Name}</h2>
                                                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: '#6d28d9' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                        <Globe size={14} />
                                                        {selectedExpert.Organisation}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                        <Briefcase size={14} />
                                                        {selectedExpert.Designation}
                                                    </div>
                                                    {(selectedExpert.Country || selectedExpert.Region) && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                            <MapPin size={14} />
                                                            {[selectedExpert.Country, selectedExpert.Region].filter(Boolean).join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            {canEdit && (
                                                <>
                                                    <button
                                                        onClick={() => setEditingFaculty(selectedExpert)}
                                                        style={{
                                                            background: '#ffffff', border: '1px solid #ddd6fe', color: '#6d28d9',
                                                            padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer',
                                                            fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.2s',
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f3ff'}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                                                    >
                                                        Edit Profile
                                                    </button>
                                                    <button
                                                        onClick={(e) => handleDelete(e, selectedExpert.FacultyID)}
                                                        style={{
                                                            background: '#fee2e2', border: '1px solid #fecaca',
                                                            color: '#dc2626', padding: '0.5rem', borderRadius: '8px', cursor: 'pointer',
                                                            display: 'flex', transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = '#fecaca'}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = '#fee2e2'}
                                                        title="Delete Expert"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Detailed Content */}
                                <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                    {/* About / Bio */}
                                    <section>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: '#1e293b' }}>
                                            <Award size={18} color="#8b5cf6" />
                                            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Professional Background</h3>
                                        </div>
                                        <div style={{
                                            background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #f1f5f9',
                                            fontSize: '0.95rem', color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap'
                                        }}>
                                            {selectedExpert.About || 'No background information provided.'}
                                        </div>
                                    </section>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                        {/* Skills */}
                                        <section>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: '#1e293b' }}>
                                                <GraduationCap size={18} color="#8b5cf6" />
                                                <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Identified Skills</h3>
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                {selectedExpert.Skills && selectedExpert.Skills.length > 0 ? selectedExpert.Skills.map(skill => (
                                                    <span
                                                        key={skill}
                                                        style={{
                                                            padding: '0.35rem 0.85rem', borderRadius: '20px', background: '#f3e8ff',
                                                            fontSize: '0.8rem', fontWeight: 600, color: '#6d28d9', border: '1px solid #ddd6fe'
                                                        }}
                                                    >
                                                        {skill}
                                                    </span>
                                                )) : (
                                                    <div style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>Pending analysis...</div>
                                                )}
                                            </div>
                                        </section>

                                        {/* Additional Info */}
                                        <section>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: '#1e293b' }}>
                                                <Globe size={18} color="#0d9488" />
                                                <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Expertise & Links</h3>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                <div style={{ background: '#f0fdfa', padding: '0.75rem', borderRadius: '10px', border: '1px solid #ccfbf1' }}>
                                                    <div style={{ fontSize: '0.75rem', color: '#134e4a', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Primary Focus</div>
                                                    <div style={{ fontSize: '0.9rem', color: '#115e59' }}>{selectedExpert.AreaOfExpertise || 'General'}</div>
                                                </div>
                                                {selectedExpert.LinkedInURL && (
                                                    <a
                                                        href={selectedExpert.LinkedInURL}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0077b5',
                                                            textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600,
                                                            background: '#f1f5f9', padding: '0.75rem', borderRadius: '10px',
                                                            transition: 'background 0.2s'
                                                        }}
                                                    >
                                                        <Linkedin size={18} />
                                                        LinkedIn Professional Profile
                                                        <ChevronRight size={14} style={{ marginLeft: 'auto' }} />
                                                    </a>
                                                )}
                                            </div>
                                        </section>
                                    </div>

                                    {/* Guest Panelist Engagement Timeline */}
                                    {(() => {
                                        const expertHistory = invitationHistory.filter(h => h.panelistId === selectedExpert.FacultyID);
                                        return (
                                            <section style={{ marginTop: '0.5rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: '#1e293b' }}>
                                                    <History size={18} color="#8b5cf6" />
                                                    <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Guest Invitation & Panelist History</h3>
                                                </div>
                                                {expertHistory.length === 0 ? (
                                                    <div style={{ padding: '1.5rem', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
                                                        No past guest invitations recorded in the system.
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', borderLeft: '2px solid #e2e8f0', paddingLeft: '1.25rem', marginLeft: '0.5rem', position: 'relative' }}>
                                                        {expertHistory.map((item, idx) => (
                                                            <div key={idx} style={{ position: 'relative' }}>
                                                                {/* Timeline Dot */}
                                                                <div style={{
                                                                    position: 'absolute', left: '-26px', top: '4px', width: '10px', height: '10px', borderRadius: '50%',
                                                                    background: item.eventStatus === 'Completed' ? '#10b981' : '#3b82f6',
                                                                    border: '2px solid white', boxShadow: '0 0 0 2px #e2e8f0'
                                                                }} />
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                                                                    <div>
                                                                        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{item.eventTitle}</div>
                                                                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                                            <span>{item.category}</span>
                                                                            <span>•</span>
                                                                            <span>{new Date(item.eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                                        </div>
                                                                    </div>
                                                                    <span style={{
                                                                        fontSize: '0.7rem', padding: '0.15rem 0.45rem', borderRadius: '99px', fontWeight: 600,
                                                                        background: item.eventStatus === 'Completed' ? '#d1e7dd' : '#bae6fd',
                                                                        color: item.eventStatus === 'Completed' ? '#0f5132' : '#0369a1',
                                                                        border: `1px solid ${item.eventStatus === 'Completed' ? '#badbcc' : '#bae6fd'}`
                                                                    }}>
                                                                        {item.eventStatus}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </section>
                                        );
                                    })()}

                                    {/* Footer Stats */}
                                    <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.75rem' }}>
                                        <span>Last Updated: {new Date(selectedExpert.UpdatedAt).toLocaleDateString()}</span>
                                        <span>Expert ID: {selectedExpert.FacultyID}</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                <div style={{ padding: '2rem', background: '#f8fafc', borderRadius: '50%', marginBottom: '1rem' }}>
                                    <Briefcase size={48} color="#e2e8f0" />
                                </div>
                                <h3>Select an Expert</h3>
                                <p>Select a profile from the left to view detailed information.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modals */}
            {(isCreating || editingFaculty) && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)',
                    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'
                }}>
                    <FacultyForm
                        initialData={editingFaculty}
                        onSuccess={handleCreateSuccess}
                        onCancel={() => {
                            setIsCreating(false);
                            setEditingFaculty(null);
                        }}
                    />
                </div>
            )}

            {/* Fullscreen Graph Modal */}
            {isFullscreen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'white', zIndex: 9999, display: 'flex', flexDirection: 'column'
                }}>
                    <div style={{
                        padding: '1rem 2rem', borderBottom: '1px solid #e2e8f0',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: '#f8fafc'
                    }}>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>Expert Network Graph</h2>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>Connections between experts and their skills.</p>
                        </div>
                        <button
                            onClick={toggleFullscreen}
                            style={{
                                background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.5rem',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)', color: '#475569', transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                        >
                            <Minimize2 size={20} />
                        </button>
                    </div>
                    <div style={{ flex: 1, position: 'relative', background: '#fafafa', overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
                        <ExpertGraph data={filteredFaculty} onNodeClick={handleGraphNodeClick} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Faculty;
